import type { SearchPayload } from '@civilgrid/ui/hooks';
import type {
  AdminTileLayer,
  NewTransactionData,
  OrganizationDetail,
  PlanType,
  Subscription,
  SubscriptionDetail,
  User,
} from '~/common/types';
import { getAxiosClient } from './common';

export interface NewOrgData {
  organizationName: string;
  billingContacts: string[];
  zoningCost: number;
  planType: PlanType;
  projectCost: Subscription['projectCost'];
  sfdcAccountId?: string | null;
  sfdcOpportunityId?: string | null;
  state?: string | null;
  excludeDemoProject?: boolean;
  isInternal?: boolean;
}

const axios = getAxiosClient();

export async function getAllOrganizations() {
  const { data } = await axios({
    method: 'GET',
    url: `/v1/internal/getAllOrganizations`,
  });
  return data;
}

export async function getAllUsers() {
  const { data } = await axios({
    method: 'GET',
    url: `/v1/internal/getAllUsers`,
  });
  return data;
}

export async function getUnapprovedUsers(): Promise<User[]> {
  const { data } = await axios.get(`/v1/internal/getAllUsers?filter=unapproved`);
  return data;
}

export async function getInternalAdminUsers(): Promise<User[]> {
  const { data } = await axios.get(`/v1/internal/getInternalUsers`);
  return data;
}

export async function setUserToOnboarded(userId: string) {
  const { data } = await axios({
    method: 'GET',
    url: `/v1/internal/setUserToOnboarded?userId=${userId}`,
  });
  return data;
}

export async function setUserToApproved(userId: string) {
  const { data } = await axios({
    method: 'GET',
    url: `/v1/internal/setUserToApproved?userId=${userId}`,
  });
  return data;
}

export async function updateOrgPlan(organizationId: string, plan: string) {
  const { data } = await axios({
    method: 'GET',
    url: `/v1/internal/updateOrgPlan?organizationId=${organizationId}&plan=${plan}`,
  });
  return data;
}

export async function createOrg(orgInfo: NewOrgData) {
  const { data } = await axios({
    method: 'POST',
    url: `/v1/internal/createOrganization`,
    data: { orgInfo },
  });

  return data;
}

export async function addTransaction(transactionInfo: NewTransactionData) {
  const { data } = await axios({
    method: 'POST',
    url: `/v1/internal/addTransaction`,
    data: { transactionInfo },
  });

  return data;
}

export async function createSandboxOrg(orgName: string) {
  const { data } = await axios({
    method: 'POST',
    url: `/v1/internal/createSandboxOrg`,
    data: { orgName },
  });

  return data;
}

export async function getDemoOrgs(): Promise<{ id: string; name: string }[]> {
  const { data } = await axios({ method: 'GET', url: `/v1/internal/getDemoOrgs` });
  return data;
}

export async function resetDemoOrg(organizationId: string): Promise<{ success: true; eventId: string }> {
  const { data } = await axios({ method: 'POST', url: `/v1/internal/resetDemoOrg`, data: { organizationId } });
  return data;
}

export async function printOrgInvoice(projectIds: string[], dateRange: string) {
  await axios({
    method: 'POST',
    url: `/v1/internal/printOrgInvoice`,
    data: { projectIds, dateRange },
  });
}

export async function toggleIsBillable(projectId: string) {
  const { data } = await axios({
    method: 'GET',
    url: `/v1/internal/toggleIsBillable?projectId=${projectId}`,
  });
  return data;
}

export async function runAgencyLoader(): Promise<{ success: true }> {
  const { data } = await axios({
    method: 'POST',
    url: `/v1/internal/runAgencyLoader`,
  });

  return data;
}

export async function clearEsriCache(): Promise<{ success: true }> {
  const { data } = await axios({
    method: 'POST',
    url: `/v1/internal/clearEsriCache`,
  });

  return data;
}

export async function refreshTiles(): Promise<{ success: true; id: string }> {
  const { data } = await axios({
    method: 'POST',
    url: `/v1/internal/refreshTiles`,
  });

  return data;
}

export async function refreshTileConfig(): Promise<{ success: true; id: string }> {
  const { data } = await axios({
    method: 'POST',
    url: `/v1/internal/refreshTileConfig`,
  });

  return data;
}

export async function loadStagingLayerCatalog(): Promise<{ rowCount: number; lastModified: string | null }> {
  const { data } = await axios({
    method: 'POST',
    url: `/v1/internal/loadStagingLayerCatalog`,
  });

  return data;
}

export async function listOrgProjectsAsAdm(organizationId: string) {
  const { data } = await axios({
    method: 'GET',
    url: `/v1/internal/listOrgProjectsAsAdm?organizationId=${organizationId}`,
  });
  return data;
}

export async function computeIntelligence(projectId: string): Promise<{ success: boolean }> {
  const { data } = await axios.post(`/v1/internal/computeIntelligence?projectId=${projectId}`);

  return data;
}

export async function syncIntelligence(): Promise<{ success: boolean }> {
  const { data } = await axios.post(`/v1/internal/syncIntelligence`);
  return data;
}

export async function syncIntelligenceForOrg(organizationId: string): Promise<{ success: boolean }> {
  const { data } = await axios.post(`/v1/internal/syncIntelligence?&organizationId=${organizationId}`);

  return data;
}

export async function getOrganization(organizationId: string): Promise<OrganizationDetail> {
  const { data } = await axios.get(`/v1/internal/organizations/${organizationId}`);

  return data;
}

export async function getSpecialDatasetSlugs(): Promise<string[]> {
  const { data } = await axios.get(`/v1/internal/specialDatasetSlugs`);

  return data.slugs;
}

export async function updateOrgSpecialDatasets(organizationId: string, specialDatasets: string[]): Promise<string[]> {
  const { data } = await axios.put(`/v1/internal/organizations/${organizationId}/specialDatasets`, { specialDatasets });

  return data.specialDatasets;
}

export async function updateOrgConnectbaseLookup(
  organizationId: string,
  connectbaseLookupEnabled: boolean,
): Promise<boolean> {
  const { data } = await axios.put(`/v1/internal/organizations/${organizationId}/connectbaseLookup`, {
    connectbaseLookupEnabled,
  });

  return data.connectbaseLookupEnabled;
}

export async function getAdminTileLayers(): Promise<AdminTileLayer[]> {
  const { data } = await axios.get(`/v1/internal/tileLayers`);

  return data.layers;
}

export async function updateDisabledTileLayers(disabledKeys: string[]): Promise<string[]> {
  const { data } = await axios.put(`/v1/internal/tileLayers/disabled`, { disabledKeys });

  return data.disabledKeys;
}

export async function getOrganizationSubscriptions(organizationId: string): Promise<SubscriptionDetail[]> {
  const { data } = await axios.get(`/v1/internal/organizations/${organizationId}/subscriptions`);

  return data;
}

export async function joinOrgAsAdmin(organizationId: string): Promise<{ success: boolean }> {
  const { data } = await axios.post(`/v1/internal/joinOrgAsAdmin?organizationId=${organizationId}`);

  return data;
}

export async function triggerDigestForProject(projectId: string, shouldSendToProjectMembers: string) {
  const { data } = await axios.post<{ success?: boolean }>(
    `/v1/internal/triggerDigestForProject?projectId=${projectId}&shouldSendToProjectMembers=${shouldSendToProjectMembers}`,
  );
  return data;
}

export async function updateLinkedOrganizations(
  organizationId: string,
  linkedOrganizationIds: string[],
): Promise<{ success: boolean }> {
  const { data } = await axios.post('/v1/internal/updateLinkedOrganizations', {
    organizationId,
    linkedOrganizationIds,
  });
  return data;
}

export async function updateOrganizationSubscription(
  organizationId: string,
  projectCost: Subscription['projectCost'],
  sfdcFields?: Pick<Subscription, 'sfdcAccountId' | 'sfdcOpportunityId'>,
  config?: Pick<Subscription, 'featureAccess' | 'enabledBillingTiers'>,
): Promise<{ success: boolean; subscription?: Subscription }> {
  const { data } = await axios({
    method: 'POST',
    url: `/v1/internal/updateOrganizationSubscription`,
    data: { organizationId, projectCost, ...sfdcFields, ...config },
  });
  return data;
}

// ---- PG&E Cloud Sync (admin) ----

export interface PgeCloudSyncFolder {
  prefix: string;
  name: string;
  fileCount: number;
  hasRequiredFiles: boolean;
  /** ISO date of the most recent file in the folder; null when the folder has no direct files */
  lastModified: string | null;
}

export async function getPgeCloudSyncFolders(): Promise<{ folders: PgeCloudSyncFolder[] }> {
  const { data } = await axios.get('/v1/internal/pgeCloudSync/folders');
  return data;
}

export async function triggerPgeCloudSyncWorkflow(
  sourcePrefix: string,
  targetOrganizationId: string,
): Promise<{ success: boolean }> {
  const { data } = await axios.post('/v1/internal/pgeCloudSync/trigger', { sourcePrefix, targetOrganizationId });
  return data;
}

export type ExportEscalation = {
  id: string;
  submissionId: string;
  escalationReason: 'restricted' | 'failed';
  escalationStatus: 'pending' | 'completed' | 'archived';
  requestedAt: string;
  projectId: string;
  projectName: string;
  requestorName: string | null;
  requestorEmail: string;
  formats: string[];
  isRunning: boolean;
};

export async function listExportEscalations(
  search: SearchPayload,
): Promise<{ items: ExportEscalation[]; count: number }> {
  const { data } = await axios.get(
    `/v1/internal/export-escalations?search=${encodeURIComponent(JSON.stringify(search))}`,
  );

  return data;
}

export type ExportEscalationUser = { id: string; name: string | null; email: string };

export type ExportEscalationDetail = {
  id: string;
  escalationReason: 'restricted' | 'failed';
  escalationStatus: 'pending' | 'completed' | 'archived';
  isRunning: boolean;
  notes: string | null;
  requestedAt: string;
  triggeredBy: ExportEscalationUser | null;
  triggeredAt: string | null;
  archivedBy: ExportEscalationUser | null;
  archivedAt: string | null;
  requestor: { name: string | null; email: string };
  project: { id: string; name: string; geometryType: string; segmentCount: number };
  organization: { id: string; name: string };
  area: number;
  formats: (string | null)[];
  layers: string[];
  projection: { title: string; value: string } | null;
  geojsonLink: string;
  extractTemplateName: string;
};

export async function getExportEscalation(id: string): Promise<ExportEscalationDetail> {
  const { data } = await axios.get(`/v1/internal/export-escalations/${id}`);

  return data;
}

export async function updateExportEscalationNotes(id: string, notes: string): Promise<{ notes: string }> {
  const { data } = await axios.patch(`/v1/internal/export-escalations/${id}/notes`, { notes });

  return data;
}

export async function archiveExportEscalation(id: string): Promise<{ escalationStatus: string }> {
  const { data } = await axios.post(`/v1/internal/export-escalations/${id}/archive`);

  return data;
}

export async function runExportEscalation(id: string): Promise<{ success: boolean }> {
  const { data } = await axios.post(`/v1/internal/export-escalations/${id}/run`);

  return data;
}
